// The legacy build: the modern one uses JavaScript newer than Chrome 140 has (Map's
// getOrInsertComputed), and draws nothing there.
import worker from 'pdfjs-dist/legacy/build/pdf.worker.min.mjs?url';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import { useTranslation } from 'react-i18next';

/** How much larger than its own size a page is drawn: sharp when zoomed in, on a phone too. */
const SCALE = 2.5;
/** How many steps it zooms in, a quarter at a time as a browser's own reader does: to 300 %. */
const STEPS = 8;
/** How far the wheel turns for one step: a mouse's notch, or a touchpad's pinch gathered up. */
const NOTCH = 50;

/**
 * A PDF read in place with pdf.js, each page drawn on its own canvas: the same in every browser,
 * nothing downloaded, the reader not the browser's own. pdf.js loads only once one is opened.
 * Zoomed in and out by its own buttons; with `onPick`, each piece of its text is pressed to take
 * it, as a booking's form puts it in a field.
 */
export function PdfView({
  url,
  title,
  onPick,
}: {
  url: string;
  title: string;
  /** A text pressed, and the whole line it is on. */
  onPick?: ((text: string, line: string) => void) | undefined;
}) {
  const { t } = useTranslation();
  const [holder, setHolder] = useState<HTMLDivElement | null>(null);
  // The pages' box as last laid out, to scroll and to read where it is.
  const pages = useRef(document.createElement('div'));
  const placed = useCallback((element: HTMLDivElement | null) => {
    setHolder(element);
    if (element) pages.current = element;
  }, []);
  const [failed, setFailed] = useState(false);
  const [zoom, setZoom] = useState(0);
  // Where a zoom keeps still: the point under the mouse, or the middle, as a share of the pages.
  const anchor = useRef<{ x: number; y: number; left: number; top: number } | null>(null);
  // How many pages it has, which is in view, and a page number being typed to go to.
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [typed, setTyped] = useState<string | null>(null);
  const picking = onPick !== undefined;

  useEffect(() => {
    if (holder === null) return;
    void import('pdfjs-dist/legacy/build/pdf.mjs')
      .then(async (pdfjs) => {
        pdfjs.GlobalWorkerOptions.workerSrc = worker;
        const pdf = await pdfjs.getDocument({ url }).promise;
        setCount(pdf.numPages);
        for (let number = 1; number <= pdf.numPages; number += 1) {
          const page = await pdf.getPage(number);
          const viewport = page.getViewport({ scale: SCALE });
          const sheet = document.createElement('div');
          sheet.className = 'pdf-sheet';
          const canvas = document.createElement('canvas');
          canvas.className = 'pdf-page';
          canvas.width = Math.floor(viewport.width);
          canvas.height = Math.floor(viewport.height);
          canvas.setAttribute('role', 'img');
          canvas.setAttribute('aria-label', t('file.page', { title, number }));
          sheet.append(canvas);
          holder.append(sheet);
          await page.render({ canvas, viewport }).promise;
          if (!picking) continue;
          // Its words laid over the page where they are drawn, each pressed to take it.
          const { items } = await page.getTextContent();
          for (const item of items) {
            if (!('str' in item) || item.str.trim() === '') continue;
            const [, , c, d, x, y] = pdfjs.Util.transform(
              viewport.transform,
              item.transform,
            ) as [number, number, number, number, number, number];
            const high = Math.hypot(c, d);
            const word = document.createElement('span');
            word.className = 'pdf-word';
            word.dataset.text = item.str.trim();
            word.title = t('file.pick', { text: item.str.trim() });
            word.style.left = `${String((x / viewport.width) * 100)}%`;
            word.style.top = `${String(((y - high) / viewport.height) * 100)}%`;
            word.style.width = `${String(((item.width * SCALE) / viewport.width) * 100)}%`;
            word.style.height = `${String((high / viewport.height) * 100)}%`;
            word.dataset.line = String(Math.round(y - high / 2));
            word.dataset.high = String(Math.round(high));
            sheet.append(word);
          }
        }
      })
      .catch(() => {
        setFailed(true);
      });
    return () => {
      holder.replaceChildren();
    };
  }, [holder, url, title, t, picking]);

  // A zoom a step in or out, keeping still the point at `x`, `y` in the pages' box.
  const step = useCallback((by: number, x?: number, y?: number) => {
    const box = pages.current;
    const left = x ?? box.clientWidth / 2;
    const top = y ?? box.clientHeight / 2;
    setZoom((was) => {
      const next = Math.min(STEPS, Math.max(0, was + by));
      if (next !== was) {
        anchor.current = {
          x: (box.scrollLeft + left) / Math.max(1, box.scrollWidth),
          y: (box.scrollTop + top) / Math.max(1, box.scrollHeight),
          left,
          top,
        };
      }
      return next;
    });
  }, []);

  // The point kept still where it was, once the pages are laid out at the new zoom.
  useLayoutEffect(() => {
    const kept = anchor.current;
    if (kept === null) return;
    anchor.current = null;
    const box = pages.current;
    box.scrollLeft = kept.x * box.scrollWidth - kept.left;
    box.scrollTop = kept.y * box.scrollHeight - kept.top;
  }, [zoom]);

  // Ctrl and the wheel zoom the pages, not the page around them: heard where it can be stopped,
  // a step a notch — a fast wheel's many at once, a touchpad's small ones gathered to one.
  useEffect(() => {
    if (holder === null) return;
    let turned = 0;
    const wheel = (event: WheelEvent) => {
      if (!event.ctrlKey) return;
      event.preventDefault();
      turned +=
        event.deltaMode === WheelEvent.DOM_DELTA_PIXEL ? event.deltaY : event.deltaY * 40;
      if (Math.abs(turned) < NOTCH) return;
      const box = holder.getBoundingClientRect();
      step(turned < 0 ? 1 : -1, event.clientX - box.left, event.clientY - box.top);
      turned = 0;
    };
    holder.addEventListener('wheel', wheel, { passive: false });
    return () => {
      holder.removeEventListener('wheel', wheel);
    };
  }, [holder, step]);

  const shown = 1 + zoom / 4;
  const sheets = () => [...pages.current.querySelectorAll<HTMLElement>('.pdf-sheet')];
  // The page whose top is above a third of the view down is the one being read.
  const follow = () => {
    const box = pages.current;
    const top = box.getBoundingClientRect().top;
    const above = sheets().filter(
      (sheet) => sheet.getBoundingClientRect().top - top <= box.clientHeight / 3,
    );
    setPage(Math.max(1, above.length));
  };
  // A page typed gone to, kept within those it has.
  const go = (to: string) => {
    const number = Math.min(count, Math.max(1, Math.round(Number(to)) || 1));
    const sheet = sheets()[number - 1];
    if (sheet) pages.current.scrollTop = sheet.offsetTop;
    setPage(number);
    setTyped(null);
  };

  return (
    <div className="pdf-view">
      {/* As a browser's own reader has it, in the middle: the page of how many, then the zoom. */}
      <div className="pdf-bar" role="toolbar" aria-label={t('file.tools')}>
        <span className="pdf-bar-pages">
          <input
            className="pdf-bar-page"
            type="text"
            inputMode="numeric"
            aria-label={t('file.pageNumber', { count })}
            value={typed ?? String(page)}
            onFocus={(event) => {
              event.currentTarget.select();
            }}
            onChange={(event) => {
              setTyped(event.target.value);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') go(event.currentTarget.value);
            }}
            onBlur={() => {
              setTyped(null);
            }}
          />
          <span aria-hidden="true">/ {count}</span>
        </span>
        <span className="pdf-bar-line" aria-hidden="true" />
        <button
          type="button"
          aria-label={t('file.smaller')}
          title={t('file.smaller')}
          disabled={zoom === 0}
          onClick={() => {
            step(-1);
          }}
        >
          −
        </button>
        <span className="pdf-bar-zoom" aria-live="polite">
          {Math.round(shown * 100)}%
        </span>
        <button
          type="button"
          aria-label={t('file.larger')}
          title={t('file.larger')}
          disabled={zoom === STEPS}
          onClick={() => {
            step(1);
          }}
        >
          +
        </button>
      </div>
      {/* Focusable, so a keyboard scrolls the pages. */}
      <div
        className="pdf-pages"
        ref={placed}
        tabIndex={0}
        role="group"
        aria-label={title}
        style={{ '--pdf-zoom': shown } as CSSProperties}
        onScroll={follow}
        onClick={(event) => {
          const word = (event.target as HTMLElement).closest<HTMLElement>('.pdf-word');
          const text = word?.dataset.text;
          if (word === null || text === undefined || !onPick) return;
          // Its line: the words of its page about as high, left to right.
          const at = Number(word.dataset.line);
          const line = [...pages.current.querySelectorAll<HTMLElement>('.pdf-word')]
            .filter(
              (other) =>
                other.parentElement === word.parentElement &&
                Math.abs(Number(other.dataset.line) - at) < Number(word.dataset.high) / 2,
            )
            .sort((one, other) => one.offsetLeft - other.offsetLeft)
            .map((other) => other.dataset.text)
            .join(' ');
          onPick(text, line);
        }}
      />
      {failed && (
        <p className="form-failed" role="alert">
          {t('file.unreadable')}
        </p>
      )}
    </div>
  );
}
